// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SummaryCard, { NothingToSummarise } from "../../shared/SummaryCard";
import StereoLadderChart from "./StereoLadderChart";
import TrialReviewSection, {
  StereoLadderModuleType,
} from "../TrialReviewSection/TrialReviewSection";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

// Utils
import { num } from "../../shared/formatters";

type SummaryType = NonNullable<
  ExamSectionReportType["postProcessing"]["summary"]
>;
type StereoLadderSummaryType = SummaryType["StereoLadder"];

type SummarySectionType = {
  summary: SummaryType | null | undefined;
  stereoLadderModule: StereoLadderModuleType;
};

// The one caution that says "disregard this threshold whatever it says": the patient picked a
// circle on a plate with no depth in it. Everything else that used to be rendered here
// described the order rather than the patient, but this one changes what the number means, so
// it survives the cautions block as a single line under the opening sentence.
const CATCH_TRIALS_FAILED = "CatchTrialsFailed";

/**
 * What the patient could see, and how the run got there.
 *
 * The card no longer interprets the number. `Threshold`, `At or Below` and `Interpretation`
 * are gone: the ladder already shows which plates were passed and where the run stopped, "at
 * or below WHAT" was never on the card, and whether a result is normal for a given patient is
 * not the report's call.
 */
const DepthPerceptionCard = ({
  data,
  stereoLadderModule,
}: {
  data: StereoLadderSummaryType;
  stereoLadderModule: StereoLadderModuleType;
}) => {
  const { t } = useTranslation();

  if (!data) {
    return (
      <SummaryCard title={t("Depth perception")} cautions={null}>
        <Box sx={{ pb: "8px" }}>
          <NothingToSummarise />
        </Box>
      </SummaryCard>
    );
  }

  const catchTrialsFailed = (data.cautions ?? []).includes(CATCH_TRIALS_FAILED);

  return (
    <SummaryCard title={t("Depth perception")} cautions={null}>
      <Box
        sx={{ pb: "8px", display: "flex", flexDirection: "column", gap: "8px" }}
      >
        <Typography fontSize="18px" fontWeight={700} sx={{ lineHeight: 1.35 }}>
          {data.thresholdArcseconds === null ||
          data.thresholdArcseconds === undefined
            ? t("The patient did not pass any plate, so no depth was measured.")
            : t("The patient can see depth down to {{value}} arcseconds.", {
                value: num(data.thresholdArcseconds, 0),
              })}
        </Typography>

        {catchTrialsFailed && (
          <Typography
            fontSize="12px"
            color="#E10C0C"
            data-testid="stereopsis-catch-trials-failed"
          >
            {t(
              "The patient picked a circle when none stood out, so treat this number as unreliable."
            )}
          </Typography>
        )}

        <Typography fontSize="12px" color="#5B6D7A">
          {t(
            "Arcseconds measure how small a depth difference the patient can still see: the smaller the number, the finer the depth perception."
          )}
        </Typography>

        <StereoLadderChart
          thresholdArcseconds={data.thresholdArcseconds}
          levels={stereoLadderModule?.levels}
          ladderArcsec={stereoLadderModule?.ladderArcsec}
        />

        <Typography fontSize="12px" color="#5B6D7A" sx={{ lineHeight: 1.5 }}>
          {t(
            "The patient works down the Titmus booklet one plate at a time and the run stops at the first plate they do not pass. Each plate shows four circles and the patient picks the one that stands out."
          )}
        </Typography>
      </Box>
    </SummaryCard>
  );
};

/**
 * The section as a clinic user reads it: what the patient could see, then the plates
 * themselves.
 *
 * No cautions list and no incomplete-modules list. `CatchTrialsFailed` is still emitted by the
 * algorithm and still rides on `summary.StereoLadder.cautions` in every submission - it is
 * read above rather than printed as a raw key.
 */
const SummarySection = ({
  summary,
  stereoLadderModule,
}: SummarySectionType) => {
  if (!summary) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <DepthPerceptionCard
        data={summary.StereoLadder}
        stereoLadderModule={stereoLadderModule}
      />
      <TrialReviewSection stereoLadder={stereoLadderModule} />
    </Box>
  );
};

export default SummarySection;
